import {
  Component,
  forwardRef,
  OnInit,
  OnChanges,
  SimpleChanges,
  Input,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  ValidationErrors,
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';

import { formatCVC } from 'src/app/core/utility/common.utils';
import * as cardValidator from 'card-validator';

@Component({
  selector: 'app-cvc-input',
  templateUrl: './cvc-input.component.html',
  styleUrls: ['./cvc-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CvcInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CvcInputComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CvcInputComponent
  implements OnInit, OnChanges, ControlValueAccessor, Validator
{
  @Input() cardBrand: string = '';
  @Input() marginless: boolean = false;

  form: FormGroup;
  maxLengthCvc: number = 4;
  minLengthCvc: number = 3;

  // ControlValueAccessor callbacks
  private onChangeFn: (value: any) => void = () => {};
  private onTouchedFn: () => void = () => {};

  constructor(private fb: FormBuilder) {}

  ngOnInit() {
    this.updateCvcLength();
    this.form = this.fb.group({
      cvc: [
        '',
        [
          Validators.required,
          Validators.minLength(this.minLengthCvc),
          Validators.maxLength(this.maxLengthCvc),
        ],
      ],
    });

    this.form.get('cvc')?.valueChanges.subscribe((value) => {
      this.updateCvcValue(value);
      this.onChangeFn(this.getFormData());
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['cardBrand'] && !changes['cardBrand'].firstChange) {
      this.updateCvcLength();
      this.updateValidators();
    }
  }

  private updateCvcLength() {
    // Use card-validator library to get CVC length based on card brand
    if (this.cardBrand) {
      const cardInfo = cardValidator.creditCardType.getTypeInfo(this.cardBrand);
      if (cardInfo?.code?.size) {
        this.maxLengthCvc = cardInfo.code.size;
        this.minLengthCvc = cardInfo.code.size;
        return;
      }
    }
    // Default: allow 3-4 digits if card brand is unknown
    this.maxLengthCvc = 4;
    this.minLengthCvc = 3;
  }

  private updateValidators() {
    const cvcControl = this.form?.get('cvc');
    if (cvcControl) {
      cvcControl.setValidators([
        Validators.required,
        Validators.minLength(this.minLengthCvc),
        Validators.maxLength(this.maxLengthCvc),
      ]);
      cvcControl.updateValueAndValidity();
    }
  }

  updateCvcValue(value: string) {
    const formattedValue = formatCVC(value);
    const shortValue = formattedValue.slice(
      0,
      this.maxLengthCvc || formattedValue.length
    );

    this.form.get('cvc')?.setValue(shortValue, { emitEvent: false });
  }

  getFormData() {
    return {
      cvc: this.form.value.cvc,
    };
  }

  // --------------------------------------------------
  // ControlValueAccessor Methods
  // --------------------------------------------------
  writeValue(value: any): void {
    if (value) {
      if (typeof value === 'string') {
        this.form.patchValue({ cvc: value }, { emitEvent: false });
      } else if (value.cvc) {
        this.form.patchValue({ cvc: value.cvc }, { emitEvent: false });
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.form.disable() : this.form.enable();
  }

  handleOnTouched() {
    if (this.onTouchedFn) {
      this.onTouchedFn();
    }
  }

  // --------------------------------------------------
  // Validator Interface Implementation
  // --------------------------------------------------
  validate(control: AbstractControl): ValidationErrors | null {
    return this.form.valid ? null : { invalidCvcInput: this.form.errors };
  }
}

